import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, catchError, throwError } from 'rxjs';
import { ToastService } from '../services/toast';
import { PartnerKycService, normalizeKycStatus } from '../services/partner-kyc';

/** Where a non-approved partner is parked until an admin approves their KYC. */
export const KYC_ROUTE = '/partner-kyc';

/**
 * The KYC gate. A non-approved partner can log in, but the API blocks every business
 * endpoint with a 403 that carries a `kycStatus` field. When we see that shape we:
 *   - record the latest status in the shared KYC state,
 *   - redirect to the KYC page,
 *   - toast the server message,
 *   - and swallow the error so the generic error interceptor doesn't also fire
 *     ("Access denied") or treat it as an auth failure / log the user out.
 *
 * Must be registered LAST so it is the innermost response handler (sees the 403 first).
 */
export const kycGateInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const toast = inject(ToastService);
  const kyc = inject(PartnerKycService);

  return next(req).pipe(
    catchError((error) => {
      const body = error?.error;
      const hasKycStatus =
        error?.status === 403 && body && typeof body === 'object' && 'kycStatus' in body;

      if (!hasKycStatus) return throwError(() => error);

      // Keep shared state in sync so the KYC page banner reflects reality.
      kyc.status.set(normalizeKycStatus(body.kycStatus));
      kyc.loaded.set(true);

      toast.warning(
        body.message || 'Your KYC is not approved yet. Access is restricted until an admin approves it.',
      );

      if (router.url.split('?')[0] !== KYC_ROUTE) {
        router.navigate([KYC_ROUTE]);
      }

      // Swallow: completing without a value stops the error from bubbling to the
      // generic error interceptor (no duplicate toast, no logout).
      return EMPTY;
    }),
  );
};
